import { MainApi } from 'api/MainApi'
import { RobotApi } from 'api/RobotApi'
import { Button } from 'components/Button/Button'
import ConfirmDialog from 'components/ConfirmDialog/ConfirmDialog'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { selectRobot } from 'store/selectors/robot'
import store from 'store/store'
import { robotThunks } from 'store/thunks/robot'
import {
  GAUSIUM_REMOTE_COMMAND_START_DELAY_MS,
  ROBOT_COMMANDS,
  ROBOT_MODES,
  ROBOT_NETWORK_STATUSES,
  TASK_RUNTIME_STATE,
} from 'utils/constants'
import { getTaskRuntimeState } from 'utils/taskRuntimeState'
import { VENDOR } from 'utils/vendorDisplayName'

import style from './RobotControlButtons.module.css'

const RobotControlButtons = () => {
  const { t } = useTranslation()
  const robot = useSelector(selectRobot)
  const [startButtonBlocked, setStartButtonBlocked] = useState(false)
  const [disableButtonBlocked, setDisableButtonBlocked] = useState(false)
  const [pendingRuntimeState, setPendingRuntimeState] = useState<TASK_RUNTIME_STATE | null>(null)

  const isRobotOffline = robot.robotInfo?.networkStatus === ROBOT_NETWORK_STATUSES.OFFLINE
  const isGausium = robot.robotInfo?.vendor === VENDOR.GAUSIUM
  const taskRuntimeState = getTaskRuntimeState(robot.robotInfo)
  const effectiveRuntimeState = pendingRuntimeState ?? taskRuntimeState

  const isNotAutomatic = robot.robotInfo?.activeMode !== ROBOT_MODES.MODE_AUTOMATIC
  const isNotSet =
    robot.robotInfo?.activeMode === ROBOT_MODES.MODE_NOT_SET || robot.robotInfo?.activeMode === ROBOT_MODES.MODE_MANUAL
  const isStartButtonDisabled =
    robot.robotInfo?.activeMode !== ROBOT_MODES.MODE_AUTOMATIC ||
    robot.robotInfo?.activeCommand === ROBOT_COMMANDS.START
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false)

  const GS_PENDING_STATE_TTL_MS = 20000

  useEffect(() => {
    if (pendingRuntimeState == null) return
    if (taskRuntimeState === pendingRuntimeState) {
      setPendingRuntimeState(null)
      return
    }
    const timer = setTimeout(() => setPendingRuntimeState(null), GS_PENDING_STATE_TTL_MS)
    return () => clearTimeout(timer)
  }, [pendingRuntimeState, taskRuntimeState])

  const onNotSetModeClicked = () => {
    if (robot.robotInfo?.id !== undefined) {
      setDisableButtonBlocked(true)
      store.dispatch(robotThunks.setRobotNotSetMode(robot.robotInfo?.id))
      setTimeout(() => {
        setDisableButtonBlocked(false)
      }, 4500)
    }
  }

  const onStartPauseClicked = async () => {
    if (!robot.robotInfo?.id) {
      return
    }
    try {
      setStartButtonBlocked(true)
      if (isGausium) {
        if (effectiveRuntimeState === TASK_RUNTIME_STATE.RUNNING) {
          setPendingRuntimeState(TASK_RUNTIME_STATE.PAUSED)
          await MainApi.postGausiumRemoteCommand(robot.robotInfo.id, {
            kind: 'pause_task',
            start_delay: GAUSIUM_REMOTE_COMMAND_START_DELAY_MS,
          })
        } else if (effectiveRuntimeState === TASK_RUNTIME_STATE.PAUSED) {
          setPendingRuntimeState(TASK_RUNTIME_STATE.RUNNING)
          await MainApi.postGausiumRemoteCommand(robot.robotInfo.id, {
            kind: 'resume_task',
            start_delay: GAUSIUM_REMOTE_COMMAND_START_DELAY_MS,
          })
        }
        await store.dispatch(robotThunks.fetchRobotSilent(robot.robotInfo.id))
      } else {
        if (robot.robotInfo?.activeCommand === ROBOT_COMMANDS.START) {
          setPendingRuntimeState(TASK_RUNTIME_STATE.PAUSED)
          await RobotApi.execCommand(robot.robotInfo?.id, ROBOT_COMMANDS.PAUSE)
        } else {
          setPendingRuntimeState(TASK_RUNTIME_STATE.RUNNING)
          await RobotApi.execCommand(robot.robotInfo?.id, ROBOT_COMMANDS.START)
        }
      }
      setTimeout(() => {
        setStartButtonBlocked(false)
      }, 4500)
    } catch (error) {
      setStartButtonBlocked(false)
    }
  }

  const onStopAdnResetClicked = async () => {
    setIsConfirmationOpen(true)
  }
  const handleConfirmStopAndReset = async () => {
    setIsConfirmationOpen(false)
    if (!robot.robotInfo?.id) return
    if (isGausium) {
      try {
        setDisableButtonBlocked(true)
        setPendingRuntimeState(TASK_RUNTIME_STATE.IDLE)
        await MainApi.postGausiumRemoteCommand(robot.robotInfo.id, {
          kind: 'stop_task',
          start_delay: GAUSIUM_REMOTE_COMMAND_START_DELAY_MS,
        })
        await store.dispatch(robotThunks.fetchRobotSilent(robot.robotInfo.id))
      } finally {
        setTimeout(() => setDisableButtonBlocked(false), 4500)
      }
      return
    }
    onNotSetModeClicked()
  }

  const gsPauseResumeDisabled =
    isRobotOffline ||
    (effectiveRuntimeState !== TASK_RUNTIME_STATE.RUNNING && effectiveRuntimeState !== TASK_RUNTIME_STATE.PAUSED)
  const gsStopDisabled =
    isRobotOffline ||
    (effectiveRuntimeState !== TASK_RUNTIME_STATE.RUNNING && effectiveRuntimeState !== TASK_RUNTIME_STATE.PAUSED)

  const pauseResumeLabel =
    isGausium && effectiveRuntimeState === TASK_RUNTIME_STATE.RUNNING
      ? t('mission_control.pause')
      : isGausium && effectiveRuntimeState === TASK_RUNTIME_STATE.PAUSED
      ? t('mission_control.start')
      : isStartButtonDisabled
      ? t('mission_control.pause')
      : t('mission_control.start')

  return (
    <>
      <div className={style.row}>
        <Button.Standart
          className={style.button}
          disabled={isGausium ? gsPauseResumeDisabled : isRobotOffline || isNotAutomatic}
          onClick={onStartPauseClicked}
          loading={startButtonBlocked}
        >
          {pauseResumeLabel}
        </Button.Standart>
        <Button.Standart
          className={style.button}
          disabled={isGausium ? gsStopDisabled : isRobotOffline || isNotSet}
          onClick={onStopAdnResetClicked}
          loading={disableButtonBlocked}
        >
          {t('mission_control.disable')}
        </Button.Standart>
      </div>
      <ConfirmDialog
        isOpen={isConfirmationOpen}
        onClose={() => setIsConfirmationOpen(false)}
        onConfirmClick={() => void handleConfirmStopAndReset()}
        title={isGausium ? 'Stop the current Gausium task?' : 'Stop the robot and reset the mission?'}
      />
    </>
  )
}

export default RobotControlButtons
